import { useState, useEffect, useContext, useCallback } from "react";
import React from "react";
import { SvgArrowRight } from "ui/svgs";
import { AuthContext } from "context/AuthContext";
import useSWR from "swr";
import { fetchPostWithParams } from "lib/fetcher";
import CameraItem from "ui/streaming/camera-item";
import { SignalContext } from "context/SignalContext";
import { useSearchParams } from "react-router-dom";
import { CameraStateSubscription } from "lib/types";
import { useCommonService } from "pages/service";
import { CameraStreamType } from "webrtc-player/common/base.js";
import { getCameras } from "./service";

export default function SiteItem({
  onActive,
  site,
  onlineCamNum,
  offlineCamNum,
  onCameraClick,
  orgId,
  locations,
  currentCamera,
  currentSite,
  withCapabilities,
  hideOffline,
}: {
  onActive: (site: any, cameras: any) => void;
  site: any;
  onlineCamNum: number;
  offlineCamNum: number;
  onCameraClick: (camera: any) => void;
  orgId: string;
  locations: any[];
  currentCamera: any;
  currentSite: any;
  withCapabilities?: any;
  hideOffline?: boolean;
}) {
  const { id: siteId, name: siteName } = site;
  const [active, setActive] = useState(false);
  const [height, setHeight] = useState("0px");
  const [shouldFetchSubscribe, setshouldFetchSubscribe] = useState(false);
  const [postDataSubscribe, setPostDataSubscribe] =
    useState<CameraStateSubscription>(null);
  const [snapshots, setSnapshots] = useState([]);
  const refContent = React.useRef<HTMLDivElement>(null);
  const { user } = useContext(AuthContext);
  const { sessionId } = useContext(SignalContext);
  const [cameraIdsWithSnapshotError, setCameraIdsWithSnapshotError] = useState<
    number[]
  >([]);
  const [dataCameras, setDataCameras] = useState(null);
  const [loadingCamera, setLoadingCamera] = useState(false);
  const { takeSnapshot } = useCommonService();
  function toggleAccordion(active: boolean) {
    setActive(active);
    if (refContent.current != null && refContent.current != undefined) {
      setHeight(!active ? "0px" : `${refContent.current.scrollHeight}px`);
    }
  }
  const rotate = active ? "rotate(90deg)" : "rotate(0)";
  //close accordion when click on another site
  useEffect(() => {
    if (currentSite?.id !== siteId) {
      toggleAccordion(false);
    }
  }, [currentSite]);
  async function handleClickSite() {
    if (active) {
      toggleAccordion(false);
    } else {
      setLoadingCamera(true);
      try {
        const res = await getCameras(siteId, orgId);
        if (res.status !== 200) {
          console.log("Failed to fetch cameras");
        }
        const cameras = res.data?.data?.filter(
          (item) => !withCapabilities || item?.capabilities?.[withCapabilities],
        );
        setDataCameras({
          data: cameras,
          page: { total: cameras.length },
        });
        setTimeout(() => toggleAccordion(true), 0);

        onActive(site, res.data);
      } finally {
        setLoadingCamera(false);
      }
    }
  }
  useEffect(() => {
    if (!dataCameras) {
      return;
    }
    setSnapshots([]);
    initialCameraAction();
  }, [dataCameras]);

  const doFetchSnapshot = (cameras) => {
    takeSnapshot(cameras, orgId, (snapshots) => {
      snapshots?.forEach((snapshot) => {
        setSnapshots((snapshots) => {
          const newSnapshots = snapshots.filter(
            (old) => old.id !== snapshot.id,
          );
          return [...newSnapshots, snapshot];
        });
      });
    });
  };

  const initialCameraAction = async () => {
    if (dataCameras?.data?.length) {
      setPostDataSubscribe({
        ids: dataCameras.data.map((objCamera: any) => objCamera.id),
        sessionId: sessionId,
        types: ["status"],
      });
      setshouldFetchSubscribe(true);
      doFetchSnapshot(dataCameras.data);
    }
  };

  //subscribe cameras to sync camera status
  useSWR(
    shouldFetchSubscribe
      ? [`/camera/cameras/sub`, user.token, orgId, postDataSubscribe]
      : null,
    ([url, token, orgId, postDataSubscribe]) =>
      fetchPostWithParams(url, token, orgId, postDataSubscribe),
    {
      revalidateOnFocus: false,
    },
  );

  const onManualRetrySnap = useCallback(() => {
    const camerasForNvr = dataCameras?.data?.filter((item) =>
      cameraIdsWithSnapshotError.includes(item.id),
    );
    setCameraIdsWithSnapshotError([]);
    doFetchSnapshot(camerasForNvr);
  }, [cameraIdsWithSnapshotError, dataCameras]);
  //camera click
  const handleClickCamera = async (objCamera) => {
    onCameraClick(objCamera);
  };
  return (
    <>
      <div className="flex items-center justify-between group/site-item">
        <button
          id="btnSite"
          className="flex items-center p-3 w-full"
          onClick={handleClickSite}
        >
          <SvgArrowRight
            width="22"
            style={{ transform: rotate, transition: "all 0.2s linear" }}
            className="fill-neutral-500 mr-1 flex-shrink-0"
          />
          <span className="text-left">{siteName}</span>
          <div className=" ml-auto">
            {loadingCamera && (
              <div
                className="text-blue-500 inline-block h-4 w-4 ml-2 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div>
            )}
            {!withCapabilities && (
              <>
                <span className="text-emerald-500 ml-2">{onlineCamNum}</span> /{" "}
                <span className="text-red-600">{offlineCamNum}</span>
              </>
            )}
          </div>
        </button>
      </div>
      <div
        ref={refContent}
        className="overflow-hidden"
        style={{ maxHeight: `${height}`, transition: "max-height 0.4s ease" }}
      >
        {dataCameras && dataCameras.data && dataCameras.page.total == 0 && (
          <div className="text-sm text-center text-neutral-400">No Cameras</div>
        )}
        <div className="grid grid-cols-2 gap-2 w-full max-w-5xl mx-auto ">
          {dataCameras?.data?.map((data: any) => {
            let unSelectable;
            let unSelectedPopText;
            if (
              locations.find(
                (item) => item.key.toString() === data.id.toString(),
              )
            ) {
              unSelectedPopText =
                "Please remove corresponding mark to place it again";
              unSelectable = true;
            } else {
              unSelectable = false;
            }
            if (currentCamera?.id === data.id) {
              unSelectedPopText = "This camera is selected";
              unSelectable = true;
            }
            if (withCapabilities && !data?.capabilities?.[withCapabilities]) {
              return null;
            }
            if (hideOffline && data.status === "offline") {
              return null;
            }
            const objSnapshot = snapshots.find((item) => item!.id! === data.id);
            const canManualRetrySnap = cameraIdsWithSnapshotError.includes(
              data.id,
            );
            return (
              <CameraItem
                objCamera={data}
                snapshot={objSnapshot}
                autoPlayCameraId={null}
                canManualRetrySnap={canManualRetrySnap}
                onManualRetrySnap={onManualRetrySnap}
                siteName={siteName}
                key={data.id}
                onClickCamera={handleClickCamera}
                unSelectable={unSelectable}
                unSelectedPopText={unSelectedPopText}
              />
            );
          })}
        </div>
      </div>
    </>
  );
}
